import type { HttpClient, HttpEvent, HttpRequestOptions, HttpResponse } from "../http/types.js";
import type { CacheStore, CachedResponse } from "./cache-store.js";
import type { Url } from "../data-model/primitives.js";
import { sha1 } from "./_fs.js";

export interface HttpCacheClientOptions {
  readonly maxAgeMs: number;
  readonly onEvent?: (e: HttpEvent) => void;
}

export class HttpCacheClient implements HttpClient {
  readonly #inner: HttpClient;
  readonly #store: CacheStore;
  readonly #opts: HttpCacheClientOptions;

  constructor(inner: HttpClient, store: CacheStore, opts: HttpCacheClientOptions) {
    this.#inner = inner;
    this.#store = store;
    this.#opts = opts;
  }

  // NOTE: Concurrent get() calls for the same URL on a cold cache will
  // double-fetch (no in-flight Promise coalescing). Acceptable in v1 because
  // every URL the downloader requests is unique; revisit if we add prefetching.
  async get(url: Url, reqOpts?: HttpRequestOptions): Promise<HttpResponse> {
    const key = sha1("GET\n" + url);
    const entry = await this.#store.get(key);
    const now = Date.now();

    if (entry && now - entry.fetchedAt.getTime() < this.#opts.maxAgeMs) {
      this.#opts.onEvent?.({ type: "cache:hit", url });
      return toResponse(entry);
    }

    if (entry && (entry.etag !== undefined || entry.lastModified !== undefined)) {
      const headers: Record<string, string> = { ...(reqOpts?.headers ?? {}) };
      if (entry.etag !== undefined) headers["if-none-match"] = entry.etag;
      if (entry.lastModified !== undefined) headers["if-modified-since"] = entry.lastModified;
      const res = await this.#inner.get(url, { ...reqOpts, headers });
      if (res.status === 304) {
        const refreshed: CachedResponse = { ...entry, fetchedAt: new Date(now) };
        await this.#store.set(key, refreshed);
        this.#opts.onEvent?.({ type: "cache:hit", url });
        return toResponse(refreshed);
      }
      if (isCacheable(res)) {
        await this.#store.set(key, fromResponse(res, now));
      }
      return res;
    }

    if (entry) {
      // Stale entry without validators is unusable; drop it before refetching.
      await this.#store.delete(key);
    }

    const res = await this.#inner.get(url, reqOpts);
    if (isCacheable(res)) {
      await this.#store.set(key, fromResponse(res, now));
    }
    return res;
  }
}

function isCacheable(res: HttpResponse): boolean {
  return res.status >= 200 && res.status < 300;
}

function fromResponse(res: HttpResponse, fetchedAtMs: number): CachedResponse {
  const etag = res.headers["etag"];
  const lastModified = res.headers["last-modified"];
  return {
    status: res.status,
    headers: { ...res.headers },
    body: res.body,
    url: res.url,
    fetchedAt: new Date(fetchedAtMs),
    ...(etag !== undefined ? { etag } : {}),
    ...(lastModified !== undefined ? { lastModified } : {}),
  };
}

function toResponse(entry: CachedResponse): HttpResponse {
  return {
    status: entry.status,
    headers: { ...entry.headers },
    body: entry.body,
    url: entry.url,
  };
}
